import React, { useState, useEffect, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db, functions } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { SHARE_COLORS } from '../utils/sharing';
import { createBackup, restoreBackup, fetchBackups, restoreFromData } from '../utils/backup';
import { isEncryptionEnabled, enableEncryption, disableEncryption, storePassphrase, getStoredPassphrase } from '../utils/encryption';
import Header from '../components/Header';

import ConfirmationDialog from '../components/ConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import '../styles.css';

function Settings({ subscriptionStatus }) {
  const [user] = useAuthState(auth);

  const [syncStatus, setSyncStatus] = useState('offline');
  const [backups, setBackups] = useState([]);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [showEncryptionDialog, setShowEncryptionDialog] = useState(false);
  const [showPassphraseDialog, setShowPassphraseDialog] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [hideGettingStarted, setHideGettingStarted] = useState(false);
  // Sharing
  const [shareVehicles, setShareVehicles] = useState([]);
  const [shareEvents, setShareEvents] = useState([]);
  const [outgoingShares, setOutgoingShares] = useState([]);
  const [shareEmail, setShareEmail] = useState('');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const [nameDrafts, setNameDrafts] = useState({});
  const navigate = useNavigate();
  const isPro = subscriptionStatus?.plan === 'pro';

  useEffect(() => {
    setSyncStatus(navigator.onLine ? 'synced' : 'offline');
  }, []);

  useEffect(() => {
    const checkEncryption = async () => {
      if (user) {
        const enabled = await isEncryptionEnabled(user, db);
        setEncryptionEnabled(enabled);
      }
    };
    checkEncryption();
  }, [user]);

  useEffect(() => {
    const fetchHideGettingStarted = async () => {
      if (user) {
        const userDocRef = doc(db, `users/${user.uid}`);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setHideGettingStarted(userDoc.data().HideGettingStarted || false);
        }
      }
    };
    fetchHideGettingStarted();
  }, [user]);

  const fetchBackupsCallback = useCallback(async () => {
    const list = await fetchBackups(user);
    setBackups(list);
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchBackupsCallback();
    }
  }, [user, fetchBackupsCallback]);

  const handleRestoreClick = (backup) => {
    setSelectedBackup(backup);
    setShowRestoreDialog(true);
  };

  const confirmRestore = async () => {
    if (selectedBackup && selectedBackup.type === 'disable-encryption') {
      await confirmDisableEncryption();
    } else if (selectedBackup) {
      setRestoringBackup(true);
      try {
        await restoreBackup(user, selectedBackup.url);
        setShowRestoreDialog(false);
        setSelectedBackup(null);
      } finally {
        setRestoringBackup(false);
      }
    }
  };

  const handleEncryptionToggle = async (e) => {
    const newValue = e.target.checked;
    if (newValue) {
      // Enabling encryption - show warning dialog first
      setShowEncryptionDialog(true);
    } else {
      // Disabling encryption - show confirmation
      setShowRestoreDialog(true);
      setSelectedBackup({ type: 'disable-encryption' });
    }
  };

  const confirmEnableEncryption = () => {
    setShowEncryptionDialog(false);
    setShowPassphraseDialog(true);
  };

  const confirmSetPassphrase = async () => {
    if (passphrase !== confirmPassphrase) {
      alert('Passphrases do not match');
      return;
    }
    if (passphrase.length < 8) {
      alert('Passphrase must be at least 8 characters long');
      return;
    }

    try {
      storePassphrase(passphrase);
      await enableEncryption(user, db);
      setEncryptionEnabled(true);
      setShowPassphraseDialog(false);
      setPassphrase('');
      setConfirmPassphrase('');
      alert('Encryption enabled. Your sessions and tire data will now be encrypted.');
    } catch (error) {
      console.error('Error enabling encryption:', error);
      alert('Failed to enable encryption');
    }
  };

  const confirmDisableEncryption = async () => {
    // Note: The user needs to manually resave sessions to decrypt them
    await disableEncryption(user, db);
    setEncryptionEnabled(false);
    setShowRestoreDialog(false);
    setSelectedBackup(null);
    alert('Encryption disabled. You must manually resave each session to decrypt the data.');
  };

  const handleGettingStartedToggle = async (e) => {
    const newValue = !e.target.checked;
    if (user) {
      const userDocRef = doc(db, `users/${user.uid}`);
      await updateDoc(userDocRef, { HideGettingStarted: newValue });
      setHideGettingStarted(newValue);
    }
  };

  const loadOutgoingShares = useCallback(async () => {
    if (!user) return;
    const q = query(collection(db, 'shares'), where('ownerUid', '==', user.uid));
    const snap = await getDocs(q);
    setOutgoingShares(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, [user]);

  useEffect(() => {
    if (!user || !isPro) return;
    const loadShareData = async () => {
      try {
        const vSnap = await getDocs(query(collection(db, `users/${user.uid}/vehicles`), orderBy('timestamp', 'desc')));
        setShareVehicles(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        const eSnap = await getDocs(query(collection(db, `users/${user.uid}/events`), orderBy('startDate', 'desc')));
        setShareEvents(eSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        await loadOutgoingShares();
      } catch (err) {
        console.error('Error loading share data:', err.message);
      }
    };
    loadShareData();
  }, [user, isPro, loadOutgoingShares]);

  const toggleId = (list, setList, id) => {
    setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  };

  const resetShareForm = () => {
    setShareEmail('');
    setSelectedVehicleIds([]);
    setSelectedEventIds([]);
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) {
      setShareFeedback({ type: 'error', text: 'Please enter an email address.' });
      return;
    }
    if (selectedVehicleIds.length === 0 && selectedEventIds.length === 0) {
      setShareFeedback({ type: 'error', text: 'Select at least one vehicle or event to share.' });
      return;
    }
    setSharing(true);
    setShareFeedback(null);
    try {
      const shareWithUser = httpsCallable(functions, 'shareWithUser');
      await shareWithUser({
        recipientEmail: shareEmail.trim(),
        vehicleIds: selectedVehicleIds,
        eventIds: selectedEventIds,
      });
      setShareFeedback({ type: 'success', text: `Shared with ${shareEmail.trim()}.` });
      resetShareForm();
      await loadOutgoingShares();
    } catch (err) {
      setShareFeedback({ type: 'error', text: err.message || 'Failed to share.' });
    } finally {
      setSharing(false);
    }
  };

  const handleEditShare = (share) => {
    setShareEmail(share.recipientEmail || '');
    setSelectedVehicleIds(share.vehicleIds || []);
    setSelectedEventIds(share.eventIds || []);
    setShareFeedback(null);
  };

  const handleSetColor = async (share, color) => {
    setSharing(true);
    try {
      const setShareColor = httpsCallable(functions, 'setShareColor');
      await setShareColor({ recipientEmail: share.recipientEmail, color });
      await loadOutgoingShares();
    } catch (err) {
      setShareFeedback({ type: 'error', text: err.message || 'Failed to set color.' });
    } finally {
      setSharing(false);
    }
  };

  const handleSetName = async (share) => {
    const name = (nameDrafts[share.id] ?? share.recipientName ?? '').trim();
    setSharing(true);
    try {
      const setShareName = httpsCallable(functions, 'setShareName');
      await setShareName({ recipientEmail: share.recipientEmail, recipientName: name });
      setNameDrafts(prev => { const next = { ...prev }; delete next[share.id]; return next; });
      await loadOutgoingShares();
    } catch (err) {
      setShareFeedback({ type: 'error', text: err.message || 'Failed to set name.' });
    } finally {
      setSharing(false);
    }
  };

  const handleRemoveShare = async (share) => {
    setSharing(true);
    try {
      const unshareWithUser = httpsCallable(functions, 'unshareWithUser');
      await unshareWithUser({ recipientEmail: share.recipientEmail });
      await loadOutgoingShares();
    } catch (err) {
      setShareFeedback({ type: 'error', text: err.message || 'Failed to remove share.' });
    } finally {
      setSharing(false);
    }
  };

  const restoreFromFile = async () => {
    if (uploadedFile) {
      setRestoringBackup(true);
      try {
        // Create backup first
        await createBackup(user, true);
        const text = await uploadedFile.text();
        const data = JSON.parse(text);
        await restoreFromData(user, data);
        setShowRestoreDialog(false);
        setUploadedFile(null);
      } finally {
        setRestoringBackup(false);
      }
    }
  };

  return (
    <div>
      <Header syncStatus={syncStatus} />
      <div className="settings-container">
        <div className="page-title">
          <h1>Settings</h1>
          <button onClick={() => navigate('/')} className="close-button">✕</button>
        </div>
        <div className="settings-card">
          <h3>Backup & Restore</h3>
          <div className="backup-cards-container">
            {backups.map(backup => (
              <div key={backup.name} className="backup-card">
                <div className="backup-card-info">
                  {new Date(backup.timeCreated).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  <br />
                  {new Date(backup.timeCreated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </div>
                <button
                  onClick={() => handleRestoreClick(backup)}
                  className="restore-button"
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
          <button onClick={async () => {
            setCreatingBackup(true);
            try {
              const result = await createBackup(user, true);
              if (result) {
                // Fetch the file and create a blob URL to force download
                const response = await fetch(result.url);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = result.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up the blob URL
                URL.revokeObjectURL(blobUrl);

                fetchBackupsCallback();
              }
            } finally {
              setCreatingBackup(false);
            }
          }} className="btn btn-save" disabled={creatingBackup}>
            {creatingBackup ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                Creating...
              </div>
            ) : (
              'Create Backup'
            )}
          </button>
          <button onClick={() => setShowFileDialog(true)} className="btn delete-button" style={{ marginTop: '10px' }}>
            Restore From File
          </button>
        </div>

        <div className="settings-card">
          <h3>Data Encryption</h3>
          <div className="setting-item">
            <div className="setting-info">
              <label htmlFor="encryption-toggle">Enable Data Encryption</label>
              <p className="setting-description">
                Encrypt your sessions and tire data with a personal passphrase. Only Pro users can enable this feature.
              </p>
            </div>
            <div className="setting-control">
              <label className={`toggle ${subscriptionStatus?.plan !== 'pro' ? 'disabled' : ''}`}>
                <input
                  type="checkbox"
                  id="encryption-toggle"
                  checked={encryptionEnabled}
                  onChange={handleEncryptionToggle}
                  disabled={subscriptionStatus?.plan !== 'pro'}
                />
                <span className="slider"></span>
              </label>
              {subscriptionStatus?.plan !== 'pro' && (
                <span className="pro-badge">PRO</span>
              )}
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3>Sharing {!isPro && <span className="pro-badge">PRO</span>}</h3>
          <p className="setting-description">
            Share your vehicles and events with another Pro user. They'll be able to
            view, edit, and duplicate the items you share. Both accounts must be Pro.
          </p>

          {!isPro ? (
            <p className="setting-description">Upgrade to Pro to share with other users.</p>
          ) : (
            <div className="sharing-editor">
              <label className="sharing-label">Recipient email</label>
              <input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="person@example.com"
                className="form-input"
              />

              <label className="sharing-label">Vehicles</label>
              <div className="sharing-select-list">
                {shareVehicles.length === 0 && <p className="setting-description">No vehicles.</p>}
                {shareVehicles.map(v => (
                  <label key={v.id} className="sharing-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedVehicleIds.includes(v.id)}
                      onChange={() => toggleId(selectedVehicleIds, setSelectedVehicleIds, v.id)}
                    />
                    {v.name || 'Unnamed Vehicle'}
                  </label>
                ))}
              </div>

              <label className="sharing-label">Events</label>
              <div className="sharing-select-list">
                {shareEvents.length === 0 && <p className="setting-description">No events.</p>}
                {shareEvents.map(ev => (
                  <label key={ev.id} className="sharing-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedEventIds.includes(ev.id)}
                      onChange={() => toggleId(selectedEventIds, setSelectedEventIds, ev.id)}
                    />
                    {ev.name || 'Unnamed Event'}
                  </label>
                ))}
              </div>

              {shareFeedback && (
                <p className={shareFeedback.type === 'error' ? 'sharing-error' : 'sharing-success'}>
                  {shareFeedback.text}
                </p>
              )}

              <button onClick={handleShare} className="btn btn-save" disabled={sharing}>
                {sharing ? 'Working...' : 'Share'}
              </button>

              {outgoingShares.length > 0 && (
                <div className="sharing-existing">
                  <h4>Currently sharing with</h4>
                  {outgoingShares.map(share => (
                    <div key={share.id} className="sharing-existing-row">
                      <div className="sharing-existing-top">
                        <div className="sharing-existing-info">
                          <strong>{share.recipientName || share.recipientEmail}</strong>
                          {share.recipientName && <span className="sharing-existing-email">{share.recipientEmail}</span>}
                          <span>{(share.vehicleIds || []).length} vehicle(s), {(share.eventIds || []).length} event(s)</span>
                        </div>
                        <div className="sharing-existing-actions">
                          <button onClick={() => handleEditShare(share)} className="btn btn-secondary">Edit</button>
                          <button onClick={() => handleRemoveShare(share)} className="btn delete-button" disabled={sharing}>Remove</button>
                        </div>
                      </div>
                      <div className="sharing-name-section">
                        <span className="sharing-color-label">Display name</span>
                        <div className="sharing-name-row">
                          <input
                            type="text"
                            className="sharing-name-input"
                            placeholder="e.g. Race Engineer"
                            value={nameDrafts[share.id] ?? share.recipientName ?? ''}
                            onChange={(e) => setNameDrafts(prev => ({ ...prev, [share.id]: e.target.value }))}
                          />
                          <button
                            onClick={() => handleSetName(share)}
                            className="btn btn-secondary"
                            disabled={sharing || (nameDrafts[share.id] ?? share.recipientName ?? '') === (share.recipientName ?? '')}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                      <div className="sharing-color-section">
                        <span className="sharing-color-label">Highlight color</span>
                        <div className="sharing-color-row">
                          <div
                            className="sharing-color-preview"
                            style={{ backgroundColor: share.color || SHARE_COLORS[0], borderColor: share.color || SHARE_COLORS[0] }}
                          >
                            3.20
                          </div>
                          <div className="sharing-swatches">
                            {SHARE_COLORS.map(c => (
                              <button
                                key={c}
                                type="button"
                                className={`sharing-swatch ${share.color === c ? 'selected' : ''}`}
                                style={{ backgroundColor: c }}
                                onClick={() => handleSetColor(share, c)}
                                disabled={sharing}
                                title={c}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="settings-card">
          <h3>Getting Started</h3>
          <div className="setting-item">
            <div className="setting-info">
              <label htmlFor="getting-started-toggle">Show Getting Started Videos</label>
              <p className="setting-description">Display helpful videos on the main page.</p>
            </div>
            <div className="setting-control">
              <label className="toggle">
                <input
                  type="checkbox"
                  id="getting-started-toggle"
                  checked={!hideGettingStarted}
                  onChange={handleGettingStartedToggle}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {showRestoreDialog && (
        <ConfirmationDialog
          message={
            selectedBackup && selectedBackup.type === 'disable-encryption'
              ? "Disabling encryption will not automatically decrypt your existing data. You must manually resave each session to decrypt it. Are you sure you want to disable encryption?"
              : "Do you want to restore to this point? A backup will also be created for this moment in time."
          }
          onConfirm={selectedBackup ? confirmRestore : restoreFromFile}
          onCancel={() => { setShowRestoreDialog(false); setSelectedBackup(null); setUploadedFile(null); }}
          loading={restoringBackup}
        />
      )}

      {showFileDialog && (
        <div className="dialog-overlay">
          <div className="dialog-content">
            <h3 className="dialog-title">
              Select Backup To Restore From
            </h3>

            <div className="file-input-container">
              <input
                type="file"
                accept=".backup,.json"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setUploadedFile(file);
                  }
                }}
                className="file-input"
              />
            </div>

            <div className="dialog-buttons">
              <button
                onClick={() => {
                  setShowFileDialog(false);
                  setUploadedFile(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (uploadedFile) {
                    setShowFileDialog(false);
                    setShowRestoreDialog(true);
                  }
                }}
                className="btn btn-save"
                disabled={!uploadedFile}
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}

      {showEncryptionDialog && (
        <ConfirmationDialog
          message={
            <div>
              <h3>⚠️ Important Warning</h3>
              <p>Enabling encryption will protect your sessions and tire data with a passphrase.</p>
              <p><strong>Critical Information:</strong></p>
              <ul style={{ textAlign: 'left', margin: '10px 0' }}>
                <li>If you lose your passphrase, your data will be permanently inaccessible</li>
                <li>The passphrase cannot be changed once set</li>
                <li>You will need to re-enter the passphrase when accessing encrypted data after clearing cache, logging out, or using a different device</li>
              </ul>
              <p>Are you sure you want to continue?</p>
            </div>
          }
          onConfirm={confirmEnableEncryption}
          onCancel={() => setShowEncryptionDialog(false)}
        />
      )}

      {showPassphraseDialog && (
        <div className="dialog-overlay">
          <div className="dialog-content">
            <h3 className="dialog-title">Set Encryption Passphrase</h3>
            <p>This passphrase will be used to encrypt your sessions and tire data. Make sure to remember it!</p>

            <div className="form-group">
              <label htmlFor="passphrase">Passphrase (min 8 characters)</label>
              <input
                type="password"
                id="passphrase"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                className="form-input"
                placeholder="Enter your passphrase"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirm-passphrase">Confirm Passphrase</label>
              <input
                type="password"
                id="confirm-passphrase"
                value={confirmPassphrase}
                onChange={(e) => setConfirmPassphrase(e.target.value)}
                className="form-input"
                placeholder="Confirm your passphrase"
              />
            </div>

            <div className="dialog-buttons">
              <button
                onClick={() => {
                  setShowPassphraseDialog(false);
                  setPassphrase('');
                  setConfirmPassphrase('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmSetPassphrase}
                className="btn btn-save"
                disabled={passphrase.length < 8 || passphrase !== confirmPassphrase}
              >
                Enable Encryption
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;